import { clamp } from 'lodash-es';

import { css, styled } from '@mui/material';

import { Divider } from '../Divider';
import Skeleton from '../Skeleton/Skeleton';
import { skeletonClasses } from '../Skeleton/theme';
import { keyValuePairsSkeletonClasses } from './theme';

import type { KeyValuePairsSkeletonOwnerState, KeyValuePairsSkeletonProps } from './types';

export default function KeyValuePairsSkeleton(props: KeyValuePairsSkeletonProps) {
  const { columns = 2, rows = 6, showDivider = true, ...others } = props;

  const ownerState: KeyValuePairsSkeletonOwnerState = {
    ...props,
    columns: clamp(columns, 1, 2),
    rows: clamp(rows, 1, 10),
    showDivider,
  };

  const rowsArray = Array(ownerState.rows).fill(1);
  const columnsArray = Array(ownerState.columns).fill(1);

  return (
    <KeyValuePairsSkeletonRoot {...others} ownerState={ownerState}>
      {rowsArray.map((_, rowIndex) => (
        <KeyValuePairsSkeletonRow key={rowIndex} ownerState={ownerState}>
          <KeyValuePairsSkeletonGroup ownerState={ownerState}>
            {columnsArray.map((_, colIndex) => (
              <KeyValuePairsSkeletonColumn key={colIndex} ownerState={ownerState}>
                <KeyValuePairsSkeletonLabel ownerState={ownerState}>
                  <Skeleton shape="text" />
                </KeyValuePairsSkeletonLabel>
                <KeyValuePairsSkeletonValue ownerState={ownerState}>
                  <Skeleton shape="text" />
                </KeyValuePairsSkeletonValue>
              </KeyValuePairsSkeletonColumn>
            ))}
          </KeyValuePairsSkeletonGroup>
          {showDivider && <KeyValuePairsSkeletonDivider margin={0} ownerState={ownerState} />}
        </KeyValuePairsSkeletonRow>
      ))}
      {showDivider && <KeyValuePairsSkeletonDivider margin={0} ownerState={ownerState} />}
    </KeyValuePairsSkeletonRoot>
  );
}

const KeyValuePairsSkeletonRoot = styled('div', {
  target: keyValuePairsSkeletonClasses.root,
})<{ ownerState: KeyValuePairsSkeletonOwnerState }>(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    border-top: ${theme.mixins.border('border/default')};
  `,
);

const KeyValuePairsSkeletonRow = styled('div', {
  target: keyValuePairsSkeletonClasses.row,
})<{ ownerState: KeyValuePairsSkeletonOwnerState }>(
  () => css`
    position: relative;
    display: flex;
    flex-direction: column;
  `,
);

const KeyValuePairsSkeletonDivider = styled(Divider, {
  target: keyValuePairsSkeletonClasses.divider,
})<{ ownerState: KeyValuePairsSkeletonOwnerState }>(
  () => css`
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
  `,
);

const KeyValuePairsSkeletonGroup = styled('div', {
  target: keyValuePairsSkeletonClasses.group,
})<{ ownerState: KeyValuePairsSkeletonOwnerState }>(
  () => css`
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    gap: 32px;
    padding: 11px 0px;
  `,
);

const KeyValuePairsSkeletonColumn = styled('div', {
  target: keyValuePairsSkeletonClasses.column,
})<{ ownerState: KeyValuePairsSkeletonOwnerState }>(
  () => css`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 16px;
    flex: 1;
  `,
);

const KeyValuePairsSkeletonLabel = styled('div', {
  target: keyValuePairsSkeletonClasses.label,
})<{ ownerState: KeyValuePairsSkeletonOwnerState }>(
  () => css`
    width: 160px;
    .${skeletonClasses.root} {
      width: 80px;
      height: 12px;
    }
  `,
);

const KeyValuePairsSkeletonValue = styled('div', {
  target: keyValuePairsSkeletonClasses.value,
})<{ ownerState: KeyValuePairsSkeletonOwnerState }>(
  () => css`
    flex: 1;

    .${skeletonClasses.root} {
      width: 100%;
      max-width: 300px;
      height: 18px;
    }
  `,
);
